import axios from 'axios';
import _ from 'lodash';

import { Modal } from '../shared';

import { handleError } from './utils';

import { EnvironmentNameType } from '../types/enums/envnametype.enum.ts';

import { Environment } from '../types/environmentTypes';

export async function getCurrentEnv() {
    try {
        const { data } =
            await axios.get<EnvironmentNameType>('/api/currentEnv');

        return data;
    } catch (err) {
        handleError('Can not fetch current env name', err, false);
        return null;
    }
}

async function getEnvironments(): Promise<Environment[]> {
    try {
        const res = await axios.get<Environment[]>('/api/environments');
        return res.data;
    } catch (e) {
        handleError('Error fetching environments', e);
        return [];
    }
}

async function getActiveEnvironment(): Promise<Environment | null> {
    const environments = await getEnvironments();

    const currentEnv = _.find(environments, (env) => {
        return env.active === true;
    });

    return currentEnv ?? null;
}

async function getAuthStatus(basepath: string): Promise<boolean> {
    try {
        const res = await axios.get(`${basepath}/v2/auth/me`);
        return res.data?.status === 'authenticated';
    } catch {
        return false;
    }
}

export function signInWithGoogle(apiPath: string): Promise<void> {
    const pollingInterval = 1000;
    const timeoutDelay = 5 * 60 * 1000;
    const basepath = apiPath.replace('/api', '');

    return new Promise<void>((resolve, reject) => {
        const windowFeatures = 'popup,width=650,height=800';
        const url = basepath + '/api/v2/auth/google';
        const popup = window.open(url, '_blank', windowFeatures);

        if (!popup) {
            return reject(
                new Error(
                    'The popup window blocked by the browser. Please, allow this website to open new windows.',
                ),
            );
        }

        const timeout = setTimeout(() => {
            clearInterval(polling);
            popup.close();

            reject(
                new Error(
                    "Couldn't authenticate within the timeout constraints",
                ),
            );
        }, timeoutDelay);

        const polling = setInterval(async () => {
            const isAuthenticated = await getAuthStatus(apiPath);

            if (isAuthenticated) {
                clearInterval(polling);
                clearTimeout(timeout);
                popup.close();
                resolve();
            }
        }, pollingInterval);
    });
}

async function selectEnv() {
    // Get current active environment
    const environments = await getEnvironments();

    const currentEnv = _.find(environments, (env) => {
        return env.active === true;
    })?.name;

    // Show target env modal
    const items = environments.map((env) => {
        return {
            label: env.name,
            value: env.name,
            disabled: env.name === currentEnv,
        };
    });
    const targetEnv = await Modal.select({
        items,
        heading: 'Choose target environment',
        okLabel: 'Continue!',
        hint: 'Choose which environment to use',
        inputName: 'env',
    });
    if (!targetEnv) {
        return null;
    }
    const targetEnvDetails = _.find(environments, (env) => {
        return env.name === targetEnv;
    });

    return targetEnvDetails;
}

async function handleEnvAuthGoogle(
    targetEnvDetails: Environment,
): Promise<Environment | null> {
    try {
        await signInWithGoogle(targetEnvDetails.basepath);

        console.log(`Signed in to ${targetEnvDetails.name} with Google Auth`);

        return targetEnvDetails;
    } catch (error) {
        handleError(
            "Couldn't authenticate in the target environment with Google Auth",
            error,
        );
    }

    return null;
}

async function handleEnvAuth(): Promise<Environment | null> {
    const targetEnvDetails = await selectEnv();

    if (!targetEnvDetails) {
        return null;
    }

    // Check auth status for target env
    const isAuthenticated = await getAuthStatus(targetEnvDetails.basepath);

    if (isAuthenticated) {
        return targetEnvDetails;
    }

    return handleEnvAuthGoogle(targetEnvDetails);
}

export { getEnvironments, getActiveEnvironment, getAuthStatus, handleEnvAuth };
